import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, Download, DollarSign, X, ArrowUpDown, ArrowUp, ArrowDown, Camera, FileText, Calendar } from 'lucide-react';
import { format } from 'date-fns';

export default function IncomePanel({ user }) {
  const [incomeList, setIncomeList] = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isIncomeWithExpensesDialogOpen, setIsIncomeWithExpensesDialogOpen] = useState(false);
  const [isManageSourcesOpen, setIsManageSourcesOpen] = useState(false);
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);
  const [isManagePaymentMethodsOpen, setIsManagePaymentMethodsOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [newSourceName, setNewSourceName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategorySource, setNewCategorySource] = useState('');
  const [newPaymentMethodName, setNewPaymentMethodName] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  
  // Tax Year selector
  const currentYear = new Date().getFullYear();
  const [selectedTaxYear, setSelectedTaxYear] = useState('all');
  const availableTaxYears = ['all', currentYear.toString(), (currentYear - 1).toString(), (currentYear - 2).toString()];
  
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    source: '',
    category: '',
    payment_method: '',
    amount: '',
    notes: ''
  });
  const [uploadingFile, setUploadingFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showReceiptViewer, setShowReceiptViewer] = useState(false);
  const [currentReceiptUrl, setCurrentReceiptUrl] = useState('');
  const [currentReceiptFilename, setCurrentReceiptFilename] = useState('');
  
  // State for Income with Expenses feature
  const [incomeWithExpensesData, setIncomeWithExpensesData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    source: '',
    category: '',
    payment_method: '',
    income_amount: '',
    income_notes: '',
    expenses: []
  });

  const canEdit = user?.role === 'owner' || user?.role === 'super_admin' || (user?.permissions?.can_edit_income);
  const isOwner = user?.role === 'owner' || user?.role === 'super_admin';

  useEffect(() => {
    fetchIncome();
    fetchIncomeSources();
    fetchIncomeCategories();
    fetchExpenseCategories();
    fetchPaymentMethods();
  }, []);

  const fetchIncomeSources = async () => {
    try {
      const response = await axios.get(`${API}/income-sources`);
      setIncomeSources(response.data);
    } catch (error) {
      toast.error('Failed to fetch income sources');
    }
  };

  const fetchIncomeCategories = async () => {
    try {
      const response = await axios.get(`${API}/income-categories`);
      setIncomeCategories(response.data);
    } catch (error) {
      toast.error('Failed to fetch income categories');
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`);
      setExpenseCategories(response.data);
    } catch (error) {
      toast.error('Failed to fetch expense categories');
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payment-methods`);
      setPaymentMethods(response.data);
    } catch (error) {
      toast.error('Failed to fetch payment methods');
    }
  };

  const fetchIncome = async () => {
    try {
      const response = await axios.get(`${API}/income`);
      setIncomeList(response.data);
    } catch (error) {
      toast.error('Failed to fetch income data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canEdit) {
      toast.error('You do not have permission to add/edit income');
      return;
    }

    try {
      let incomeId;
      if (editingIncome) {
        await axios.put(`${API}/income/${editingIncome.id}`, formData);
        incomeId = editingIncome.id;
        toast.success('Income updated successfully');
      } else {
        const response = await axios.post(`${API}/income`, formData);
        incomeId = response.data.id;
        toast.success('Income added successfully');
      }

      // Upload file if selected
      if (selectedFile && incomeId) {
        await handleFileUploadForIncome(incomeId, selectedFile);
      }

      setIsDialogOpen(false);
      setEditingIncome(null);
      setSelectedFile(null);
      resetForm();
      fetchIncome();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save income');
    }
  };

  const handleFileUploadForIncome = async (incomeId, file) => {
    const formDataFile = new FormData();
    formDataFile.append('file', file);

    try {
      await axios.post(`${API}/upload/income/${incomeId}`, formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Receipt uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload receipt');
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!canEdit) {
      toast.error('You do not have permission to delete income');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this income entry?')) return;

    try {
      await axios.delete(`${API}/income/${id}`);
      toast.success('Income deleted successfully');
      fetchIncome();
    } catch (error) {
      toast.error('Failed to delete income');
    }
  };

  const handleEdit = (income) => {
    setEditingIncome(income);
    setFormData({
      date: income.date,
      source: income.source,
      category: income.category,
      payment_method: income.payment_method,
      amount: income.amount,
      notes: income.notes || ''
    });
    setIsDialogOpen(true);
  };

  const handleFileUpload = async (incomeId, file) => {
    if (!canEdit) {
      toast.error('You do not have permission to upload files');
      return;
    }

    setUploadingFile(incomeId);
    const formDataFile = new FormData();
    formDataFile.append('file', file);

    try {
      await axios.post(`${API}/upload/income/${incomeId}`, formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Receipt uploaded successfully');
      fetchIncome();
    } catch (error) {
      toast.error('Failed to upload receipt');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleFileDownload = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API}/download/${fileId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  const handleViewReceipt = async (fileId, filename) => {
    try {
      // Fetch the file with authentication
      const response = await axios.get(`${API}/download/${fileId}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Determine MIME type based on filename
      const isPdf = filename && filename.toLowerCase().endsWith('.pdf');
      const mimeType = isPdf ? 'application/pdf' : 'image/jpeg';
      
      // Create a blob URL with correct MIME type
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      setCurrentReceiptUrl(blobUrl);
      setCurrentReceiptFilename(filename || '');
      setShowReceiptViewer(true);
    } catch (error) {
      toast.error('Failed to load receipt');
    }
  };

  const resetForm = () => {
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      source: '',
      category: '',
      payment_method: '',
      amount: '',
      notes: ''
    });
  };

  const resetIncomeWithExpensesForm = () => {
    setIncomeWithExpensesData({
      date: format(new Date(), 'yyyy-MM-dd'),
      source: '',
      category: '',
      payment_method: '',
      income_amount: '',
      income_notes: '',
      expenses: []
    });
  };

  const addExpenseRow = () => {
    setIncomeWithExpensesData({
      ...incomeWithExpensesData,
      expenses: [
        ...incomeWithExpensesData.expenses,
        { vendor: '', category: '', amount: '', notes: '' }
      ]
    });
  };

  const removeExpenseRow = (index) => {
    const newExpenses = incomeWithExpensesData.expenses.filter((_, i) => i !== index);
    setIncomeWithExpensesData({
      ...incomeWithExpensesData,
      expenses: newExpenses
    });
  };

  const updateExpenseRow = (index, field, value) => {
    const newExpenses = [...incomeWithExpensesData.expenses];
    newExpenses[index][field] = value;
    setIncomeWithExpensesData({
      ...incomeWithExpensesData,
      expenses: newExpenses
    });
  };

  const calculateNetIncome = () => {
    const grossIncome = parseFloat(incomeWithExpensesData.income_amount) || 0;
    const totalExpenses = incomeWithExpensesData.expenses.reduce((sum, exp) => {
      return sum + (parseFloat(exp.amount) || 0);
    }, 0);
    return grossIncome - totalExpenses;
  };

  const handleSubmitIncomeWithExpenses = async (e) => {
    e.preventDefault();

    if (!canEdit) {
      toast.error('You do not have permission to add income or expenses');
      return;
    }

    // Validation
    if (!incomeWithExpensesData.source) {
      toast.error('Please select an income source');
      return;
    }

    if (!incomeWithExpensesData.income_amount || parseFloat(incomeWithExpensesData.income_amount) <= 0) {
      toast.error('Please enter a valid income amount');
      return;
    }

    if (incomeWithExpensesData.expenses.length === 0) {
      toast.error('Please add at least one associated expense');
      return;
    }

    // Validate each expense
    for (let i = 0; i < incomeWithExpensesData.expenses.length; i++) {
      const exp = incomeWithExpensesData.expenses[i];
      if (!exp.vendor) {
        toast.error(`Please enter vendor for expense ${i + 1}`);
        return;
      }
      if (!exp.amount || parseFloat(exp.amount) <= 0) {
        toast.error(`Please enter valid amount for expense ${i + 1}`);
        return;
      }
    }

    try {
      const response = await axios.post(`${API}/income-with-expenses`, incomeWithExpensesData);
      toast.success(response.data.message || 'Income and expenses created successfully');
      
      setIsIncomeWithExpensesDialogOpen(false);
      resetIncomeWithExpensesForm();
      fetchIncome();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create income with expenses');
    }
  };

  const handleAddSource = async () => {
    if (!newSourceName.trim()) {
      toast.error('Please enter a source name');
      return;
    }

    try {
      await axios.post(`${API}/income-sources`, { name: newSourceName.trim() });
      toast.success('Income source added successfully');
      setNewSourceName('');
      fetchIncomeSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add source');
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('Are you sure you want to delete this income source?')) return;

    try {
      await axios.delete(`${API}/income-sources/${sourceId}`);
      toast.success('Income source deleted successfully');
      fetchIncomeSources();
    } catch (error) {
      toast.error('Failed to delete income source');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    if (!newCategorySource) {
      toast.error('Please select a revenue category');
      return;
    }

    try {
      const response = await axios.post(`${API}/income-categories`, { 
        name: newCategoryName.trim(),
        source: newCategorySource
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Customer/Source added successfully');
      const addedName = newCategoryName.trim();
      setNewCategoryName('');
      setNewCategorySource('');
      setIsManageCategoriesOpen(false);
      await fetchIncomeCategories();
      // Auto-select the newly added category with its source
      setFormData({ 
        ...formData, 
        category: addedName,
        source: newCategorySource
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this income category?')) return;

    try {
      await axios.delete(`${API}/income-categories/${categoryId}`);
      toast.success('Income category deleted successfully');
      fetchIncomeCategories();
    } catch (error) {
      toast.error('Failed to delete income category');
    }
  };

  const handleAddPaymentMethod = async () => {
    if (!newPaymentMethodName.trim()) {
      toast.error('Please enter a payment method name');
      return;
    }

    try {
      await axios.post(`${API}/payment-methods`, { name: newPaymentMethodName.trim() });
      toast.success('Payment method added successfully');
      setNewPaymentMethodName('');
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment method');
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) return;

    try {
      await axios.delete(`${API}/payment-methods/${methodId}`);
      toast.success('Payment method deleted successfully');
      fetchPaymentMethods();
    } catch (error) {
      toast.error('Failed to delete payment method');
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedIncomeList = () => {
    // First filter by selected tax year
    let filtered = [...incomeList];
    if (selectedTaxYear !== 'all') {
      filtered = filtered.filter(income => {
        if (!income.date) return false;
        return income.date.startsWith(selectedTaxYear);
      });
    }
    
    const sorted = filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';

      // Sort by amount (number)
      if (sortConfig.key === 'amount') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Sort by date
      if (sortConfig.key === 'date') {
        return sortConfig.direction === 'asc' 
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }

      // Sort by string (source, category, payment_method, notes)
      const comparison = aValue.toString().localeCompare(bValue.toString());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    return sorted;
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  // Calculate total income based on filtered list
  const filteredIncomeList = selectedTaxYear === 'all' 
    ? incomeList 
    : incomeList.filter(income => income.date && income.date.startsWith(selectedTaxYear));
  const totalIncome = filteredIncomeList.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="space-y-6" data-testid="income-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-3xl font-bold text-slate-900">Income</h2>
          <p className="text-xs md:text-base text-slate-600 mt-1">Track your revenue sources</p>
        </div>
        <div className="flex items-center gap-2 md:gap-4 flex-wrap">
          {/* Tax Year Selector */}
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-3 py-1.5 border border-slate-700">
            <Calendar className="w-4 h-4 text-cyan-400" />
            <span className="text-xs md:text-sm text-slate-400">Year:</span>
            <Select value={selectedTaxYear} onValueChange={setSelectedTaxYear}>
              <SelectTrigger className="w-20 md:w-24 h-8 border-0 bg-transparent text-cyan-400 font-medium focus:ring-0 text-xs md:text-sm" data-testid="income-year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTaxYears.map(year => (
                  <SelectItem key={year} value={year}>
                    {year === 'all' ? 'All Years' : year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {canEdit && (
            <div className="flex gap-2">
              <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEditingIncome(null);
                setSelectedFile(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-xs md:text-sm" data-testid="add-income-button">
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add</span> Income
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="income-dialog">
              <DialogHeader>
                <DialogTitle>{editingIncome ? 'Edit Income' : 'Add New Income'}</DialogTitle>
                <DialogDescription>Enter income details below</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    data-testid="income-date-input"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Customer/Source (Optional)</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => {
                      if (value === '__add_new__') {
                        setIsManageCategoriesOpen(true);
                      } else {
                        // Find the selected customer and auto-populate their source
                        const selectedCustomer = incomeCategories.find(c => c.name === value);
                        setFormData({ 
                          ...formData, 
                          category: value,
                          source: selectedCustomer?.source || formData.source
                        });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="income-category-select">
                      <SelectValue placeholder="Select customer/source (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {incomeCategories.map((category) => (
                        <SelectItem key={category.id} value={category.name} data-testid={`category-option-${category.name}`}>
                          {category.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add_new__" className="text-blue-600 font-medium">
                        + Add New Customer/Source
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source">Type of Income</Label>
                  <Select
                    value={formData.source}
                    onValueChange={(value) => setFormData({ ...formData, source: value })}
                    required
                  >
                    <SelectTrigger data-testid="income-source-select">
                      <SelectValue placeholder="Select type of income" />
                    </SelectTrigger>
                    <SelectContent>
                      {incomeSources.map((source) => (
                        <SelectItem key={source.id} value={source.name} data-testid={`source-option-${source.name}`}>
                          {source.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment-method">Payment</Label>
                  <Select
                    value={formData.payment_method}
                    onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                  >
                    <SelectTrigger data-testid="payment-method-select">
                      <SelectValue placeholder="Select payment method (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((method) => (
                        <SelectItem key={method.id} value={method.name} data-testid={`payment-method-option-${method.name}`}>
                          {method.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount</Label>
                  <Input
                    id="amount"
                    data-testid="income-amount-input"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="income-notes-input"
                    placeholder="Additional notes..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="receipt-upload">Receipt/Invoice (Optional)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="receipt-upload"
                      data-testid="income-receipt-upload"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="flex-1"
                    />
                    <label htmlFor="camera-capture">
                      <input
                        id="camera-capture"
                        data-testid="income-camera-capture"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        as="span"
                        className="gap-1"
                      >
                        <Camera className="w-4 h-4" />
                        Photo
                      </Button>
                    </label>
                    {selectedFile && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedFile(null)}
                        data-testid="clear-receipt"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  {selectedFile && (
                    <p className="text-sm text-slate-600">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                  {editingIncome?.receipt_filename && !selectedFile && (
                    <p className="text-sm text-emerald-600">
                      Current receipt: {editingIncome.receipt_filename}
                    </p>
                  )}
                </div>
                <Button type="submit" className="w-full" data-testid="income-submit-button">
                  {editingIncome ? 'Update Income' : 'Add Income'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isIncomeWithExpensesDialogOpen} onOpenChange={(open) => {
            setIsIncomeWithExpensesDialogOpen(open);
            if (!open) {
              resetIncomeWithExpensesForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-xs md:text-sm hidden md:flex" 
                data-testid="add-income-with-expenses-button"
              >
                <Plus className="w-4 h-4" />
                Add Income with Expenses
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="income-with-expenses-dialog">
              <DialogHeader>
                <DialogTitle>Add Income with Associated Expenses</DialogTitle>
                <DialogDescription>
                  Record gross income and expenses in a single transaction (e.g., $10,000 earned - $2,000 club fee - $1,000 tip out)
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmitIncomeWithExpenses} className="space-y-6">
                {/* Income Section */}
                <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50">
                  <h3 className="text-lg font-semibold mb-4 text-emerald-900">Income Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="income-date">Date</Label>
                      <Input
                        id="income-date"
                        type="date"
                        value={incomeWithExpensesData.date}
                        onChange={(e) => setIncomeWithExpensesData({ ...incomeWithExpensesData, date: e.target.value })}
                        required
                        data-testid="income-with-expenses-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="income-category">Customer/Source (Optional)</Label>
                      <Select
                        value={incomeWithExpensesData.category}
                        onValueChange={(value) => setIncomeWithExpensesData({ ...incomeWithExpensesData, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select customer/source (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {incomeCategories.map((category) => (
                            <SelectItem key={category.id} value={category.name}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="income-source">Type of Income</Label>
                      <Select
                        value={incomeWithExpensesData.source}
                        onValueChange={(value) => setIncomeWithExpensesData({ ...incomeWithExpensesData, source: value })}
                        required
                      >
                        <SelectTrigger data-testid="income-with-expenses-source">
                          <SelectValue placeholder="Select type of income" />
                        </SelectTrigger>
                        <SelectContent>
                          {incomeSources.map((source) => (
                            <SelectItem key={source.id} value={source.name}>
                              {source.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="income-payment">Payment Method (Optional)</Label>
                      <Select
                        value={incomeWithExpensesData.payment_method}
                        onValueChange={(value) => setIncomeWithExpensesData({ ...incomeWithExpensesData, payment_method: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select payment method" />
                        </SelectTrigger>
                        <SelectContent>
                          {paymentMethods.map((method) => (
                            <SelectItem key={method.id} value={method.name}>
                              {method.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="income-amount">Gross Income Amount</Label>
                      <Input
                        id="income-amount"
                        type="number"
                        step="0.01"
                        placeholder="10000.00"
                        value={incomeWithExpensesData.income_amount}
                        onChange={(e) => setIncomeWithExpensesData({ ...incomeWithExpensesData, income_amount: e.target.value })}
                        required
                        data-testid="income-with-expenses-amount"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="income-notes">Notes (Optional)</Label>
                      <Input
                        id="income-notes"
                        placeholder="Additional notes..."
                        value={incomeWithExpensesData.income_notes}
                        onChange={(e) => setIncomeWithExpensesData({ ...incomeWithExpensesData, income_notes: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Associated Expenses Section */}
                <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-red-900">Associated Expenses</h3>
                    <Button
                      type="button"
                      onClick={addExpenseRow}
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      data-testid="add-expense-row-button"
                    >
                      <Plus className="w-4 h-4" />
                      Add Expense
                    </Button>
                  </div>
                  
                  {incomeWithExpensesData.expenses.length === 0 ? (
                    <p className="text-sm text-slate-600 text-center py-4">
                      No expenses added yet. Click "Add Expense" to add associated expenses.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {incomeWithExpensesData.expenses.map((expense, index) => (
                        <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 bg-white rounded border">
                          <div className="space-y-1">
                            <Label className="text-xs">Vendor</Label>
                            <Input
                              placeholder="Vendor name"
                              value={expense.vendor}
                              onChange={(e) => updateExpenseRow(index, 'vendor', e.target.value)}
                              required
                              data-testid={`expense-vendor-${index}`}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Category</Label>
                            <Select
                              value={expense.category}
                              onValueChange={(value) => updateExpenseRow(index, 'category', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {expenseCategories.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.name}>
                                    {cat.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Amount</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={expense.amount}
                              onChange={(e) => updateExpenseRow(index, 'amount', e.target.value)}
                              required
                              data-testid={`expense-amount-${index}`}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Notes</Label>
                            <Input
                              placeholder="Notes"
                              value={expense.notes}
                              onChange={(e) => updateExpenseRow(index, 'notes', e.target.value)}
                            />
                          </div>
                          <div className="flex items-end">
                            <Button
                              type="button"
                              onClick={() => removeExpenseRow(index)}
                              variant="destructive"
                              size="sm"
                              className="w-full"
                              data-testid={`remove-expense-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Net Income Calculation */}
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h3 className="text-lg font-semibold mb-3 text-blue-900">Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700">Gross Income:</span>
                      <span className="font-semibold text-emerald-700" data-testid="gross-income-display">
                        ${(parseFloat(incomeWithExpensesData.income_amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700">Total Expenses:</span>
                      <span className="font-semibold text-red-700" data-testid="total-expenses-display">
                        ${incomeWithExpensesData.expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t-2 border-blue-300">
                      <span className="text-slate-900">Net Income:</span>
                      <span className={`${calculateNetIncome() >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="net-income-display">
                        ${calculateNetIncome().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full" data-testid="submit-income-with-expenses">
                  Create Income and Expenses
                </Button>
              </form>
            </DialogContent>
          </Dialog>
            </div>
          )}
        </div>
      </div>

      <Card className="border-0 shadow-lg" data-testid="income-summary-card">
        <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-slate-800">Total Income</CardTitle>
              <CardDescription className="text-slate-600">{selectedTaxYear === 'all' ? 'All time' : selectedTaxYear} revenue</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-8 h-8 text-emerald-600" />
              <span className="text-3xl font-bold text-emerald-700" data-testid="total-income">
                ${totalIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile Card View */}
          <div className="md:hidden">
            {incomeList.length === 0 ? (
              <div className="text-center py-8 px-4 text-slate-500">
                No income entries yet. {canEdit && 'Click "Add Income" to get started.'}
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {getSortedIncomeList().map((income) => (
                  <Card key={income.id} className="border-l-4 border-l-emerald-500" data-testid={`income-row-${income.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-900">{income.source}</div>
                          <div className="text-xs text-slate-500">{income.date}</div>
                        </div>
                        <div className="text-lg font-bold text-emerald-600">
                          ${income.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-600">Category:</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {income.category || 'Uncategorized'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-600">Payment:</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {income.payment_method || 'Not specified'}
                          </span>
                        </div>
                        {income.notes && (
                          <div className="text-xs text-slate-600">
                            <span className="font-medium">Notes:</span> {income.notes}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                        {income.receipt_filename && income.receipt_filename !== 'None' ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewReceipt(income.receipt_file_id, income.receipt_filename)}
                              data-testid={`view-receipt-${income.id}`}
                              className="flex-1"
                              title="View receipt"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleFileDownload(income.receipt_file_id, income.receipt_filename)}
                              data-testid={`download-receipt-${income.id}`}
                              className="flex-1"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </>
                        ) : canEdit ? (
                          <label className="flex-1">
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => handleFileUpload(income.id, e.target.files[0])}
                              disabled={uploadingFile === income.id}
                              data-testid={`upload-receipt-${income.id}`}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={uploadingFile === income.id}
                              as="span"
                              className="w-full"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Upload
                            </Button>
                          </label>
                        ) : null}
                        
                        {canEdit && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(income)}
                              data-testid={`edit-income-${income.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(income.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-income-${income.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center">
                      Date
                      <SortIcon column="date" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center">
                      Source
                      <SortIcon column="category" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('source')}
                  >
                    <div className="flex items-center">
                      Category
                      <SortIcon column="source" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('payment_method')}
                  >
                    <div className="flex items-center">
                      Payment
                      <SortIcon column="payment_method" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center justify-end">
                      Amount
                      <SortIcon column="amount" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('notes')}
                  >
                    <div className="flex items-center">
                      Notes
                      <SortIcon column="notes" />
                    </div>
                  </TableHead>
                  <TableHead>Receipt</TableHead>
                  {canEdit && <TableHead className="text-center">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {incomeList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 8 : 7} className="text-center py-8 text-slate-500">
                      No income entries yet. {canEdit && 'Click "Add Income" to get started.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  getSortedIncomeList().reduce((rows, income, index, array) => {
                    const currentYear = income.date ? income.date.substring(0, 4) : '';
                    const prevYear = index > 0 && array[index - 1].date ? array[index - 1].date.substring(0, 4) : currentYear;
                    
                    // Add year divider if year changes
                    if (index > 0 && currentYear !== prevYear) {
                      rows.push(
                        <TableRow key={`year-divider-${currentYear}-${index}`} className="bg-transparent">
                          <TableCell colSpan={canEdit ? 8 : 7} className="py-2 px-0">
                            <div className="flex items-center gap-3">
                              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
                              <span className="text-xs font-semibold text-cyan-400 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                                {currentYear}
                              </span>
                              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    rows.push(
                    <TableRow key={income.id} data-testid={`income-row-${income.id}`}>
                      <TableCell className="font-medium">{income.date}</TableCell>
                      <TableCell>{income.category || 'Uncategorized'}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {income.source}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {income.payment_method || 'Not specified'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        ${income.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{income.notes || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {income.receipt_filename && income.receipt_filename !== 'None' ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewReceipt(income.receipt_file_id, income.receipt_filename)}
                                data-testid={`view-receipt-${income.id}`}
                                title="View receipt"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleFileDownload(income.receipt_file_id, income.receipt_filename)}
                                data-testid={`download-receipt-${income.id}`}
                                title="Download receipt"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(income)}
                              data-testid={`edit-income-${income.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(income.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-income-${income.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                    );
                    return rows;
                  }, [])
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Manage dialogs removed - only Super Admin can manage sources/categories through Employment Types panel */}
      {false && (
        <Dialog open={false} onOpenChange={() => {}}>
          <DialogContent data-testid="manage-sources-dialog">
            <DialogHeader>
              <DialogTitle>Manage Income Sources</DialogTitle>
              <DialogDescription>Add or remove income source options</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Add new source */}
              <div className="flex gap-2">
                <Input
                  placeholder="Enter new income source"
                  value={newSourceName}
                  onChange={(e) => setNewSourceName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSource()}
                  data-testid="new-source-input"
                />
                <Button 
                  onClick={handleAddSource}
                  data-testid="add-source-button"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* List of sources */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {incomeSources.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No income sources yet</p>
                ) : (
                  incomeSources.map((source) => (
                    <div
                      key={source.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                      data-testid={`source-item-${source.name}`}
                    >
                      <span className="font-medium text-slate-900">{source.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSource(source.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-source-${source.name}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Manage Income Categories Dialog */}
      <Dialog open={isManageCategoriesOpen} onOpenChange={setIsManageCategoriesOpen}>
        <DialogContent data-testid="manage-income-categories-dialog">
          <DialogHeader>
            <DialogTitle>Manage Customer/Source Options</DialogTitle>
            <DialogDescription>Add new customer and select their revenue category</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add new category */}
            <div className="space-y-2">
              <Label>Customer/Source Name</Label>
              <Input
                placeholder="Enter new customer/source"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                data-testid="new-income-category-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Revenue Category (Required)</Label>
              <Select
                value={newCategorySource}
                onValueChange={setNewCategorySource}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select revenue category" />
                </SelectTrigger>
                <SelectContent>
                  {incomeSources.map((source) => (
                    <SelectItem key={source.id} value={source.name}>
                      {source.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleAddCategory}
              className="w-full"
              data-testid="add-income-category-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Customer/Source
            </Button>

            {/* List of categories */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {incomeCategories.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No customer/source options yet</p>
              ) : (
                incomeCategories.map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                    data-testid={`income-category-item-${category.name}`}
                  >
                    <span className="font-medium text-slate-900">{category.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-income-category-${category.name}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Payment Methods Dialog removed - only Super Admin can manage payment methods through Employment Types panel */}

      {/* Receipt Viewer Dialog */}
      <Dialog open={showReceiptViewer} onOpenChange={(open) => {
        setShowReceiptViewer(open);
        if (!open && currentReceiptUrl) {
          // Clean up blob URL when closing dialog
          window.URL.revokeObjectURL(currentReceiptUrl);
          setCurrentReceiptUrl('');
          setCurrentReceiptFilename('');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Receipt</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center overflow-auto" style={{ minHeight: '400px' }}>
            {currentReceiptUrl && (
              <>
                {currentReceiptFilename && currentReceiptFilename.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={currentReceiptUrl}
                    className="w-full h-[70vh] border-0"
                    title="Receipt PDF"
                  />
                ) : (
                  <img 
                    src={currentReceiptUrl} 
                    alt="Receipt" 
                    className="max-w-full h-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Unable to load receipt</text></svg>';
                    }}
                  />
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
